import { postInfluencerOAuthConnect, postInfluencerFromConnectedInstagram } from '../../redux/buzzbridge/actions';
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatchPromise, showToastMessage } from '../../utils/AppUtils';
import {
    exchangeMetaOAuth,
    getInstagramAutomationOAuthUrl,
    getInstagramAutomationStatus,
} from '../../redux/socialPosts/action';
import type { InstagramAutomationStatusPayload, SocialPlatform } from '../../types/instagramAutomation.types';

import { resolveInstagramConnectProvider } from '../../utils/instagramOAuthProvider';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { openMetaOAuthPopup } from './openMetaOAuthPopup';
import { api } from '../../configurations/api';
import { httpRequest } from '../../redux/types';
import { socialLoginConfirmAuthUrl } from '../../configurations/api/url';
import { IG_AUTO_BG, IG_AUTO_BORDER, IG_AUTO_GOLD, IG_AUTO_MUTED, IG_AUTO_WHITE } from './instagramAutomationTheme';

const PLATFORM_LABELS: Record<SocialPlatform, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

export interface PlatformConnectionSettingsModalProps {
    open: boolean;
    onClose: () => void;
    platform: SocialPlatform;
    onUpdated?: () => void;
    status?: InstagramAutomationStatusPayload | null;
    hasInfluencerProfile?: boolean;
    username?: string | null;
    onOAuthCode?: (code: string, provider: string) => Promise<void>;
    authBaseUrl?: string;
    showCreatorProfileOption?: boolean;
}

export function PlatformConnectionSettingsModal({
    open,
    onClose,
    platform,
    onUpdated,
    status: statusProp,
    hasInfluencerProfile: hasProfileProp,
    username,
    onOAuthCode,
    authBaseUrl,
    showCreatorProfileOption = true,
}: PlatformConnectionSettingsModalProps) {
    const dispatchPromise = useDispatchPromise();
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const igConnectProvider = platform === 'instagram' ? resolveInstagramConnectProvider(buzzBridgeConfig) : platform;

    const [status, setStatus] = useState<InstagramAutomationStatusPayload | null>(statusProp ?? null);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [busy, setBusy] = useState(false);
    const [hasProfile, setHasProfile] = useState(Boolean(hasProfileProp));
    const [linkCreatorProfile, setLinkCreatorProfile] = useState(true);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        const applyHasProfile = (value: boolean) => {
            if (cancelled) return;
            setHasProfile(value);
            setLinkCreatorProfile(true);
        };

        if (typeof hasProfileProp === 'boolean') {
            applyHasProfile(hasProfileProp);
        }

        if (statusProp) {
            setStatus(statusProp);
            if (typeof hasProfileProp !== 'boolean') {
                applyHasProfile(Boolean(statusProp.has_influencer_profile));
            }
            return;
        }

        setLoadingStatus(true);
        dispatchPromise({
            type: getInstagramAutomationStatus.type,
            payload: { skipRefresh: true },
        })
            .then((s: unknown) => {
                if (cancelled) return;
                const next = s as InstagramAutomationStatusPayload;
                setStatus(next);
                if (typeof hasProfileProp !== 'boolean') {
                    applyHasProfile(Boolean(next?.has_influencer_profile));
                }
            })
            .catch(() => {
                if (!cancelled) setStatus(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingStatus(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, statusProp, hasProfileProp, dispatchPromise]);

    const label = PLATFORM_LABELS[platform];
    const displayUsername = username
        ? `@${String(username).replace(/^@/, '')}`
        : status?.platform_username && platform === 'instagram'
        ? `@${String(status.platform_username).replace(/^@/, '')}`
        : null;

    const needsReconnect =
        platform === 'instagram' &&
        Boolean(
            status?.needs_reconnect ||
                status?.facebook_upgrade_required ||
                (status?.instagram_connected && !status?.automation_scopes_granted),
        );

    const oauthProvider = platform === 'instagram' ? 'meta' : platform;

    const finishSuccess = (message: string) => {
        showToastMessage(message, 'success');
        if (linkCreatorProfile && showCreatorProfileOption) {
            setHasProfile(true);
        }
        onUpdated?.();
        onClose();
    };

    const exchangeTokensOnly = async (code: string, provider: string) => {
        if (provider === 'meta' || provider === 'instagram') {
            await dispatchPromise({
                type: exchangeMetaOAuth.type,
                payload: { code },
            });
            return;
        }
        await api(`${socialLoginConfirmAuthUrl}/exchange`, httpRequest.POST, { provider, code });
    };

    const handleCode = async (code: string, provider: string) => {
        setBusy(true);
        try {
            if (onOAuthCode) {
                await onOAuthCode(code, provider);
                finishSuccess(`${label} reconnected.`);
                return;
            }
            if (showCreatorProfileOption && linkCreatorProfile) {
                await dispatchPromise(postInfluencerOAuthConnect({ code, provider }));
                finishSuccess(
                    hasProfile
                        ? `${label} reconnected and updated on your creator profile.`
                        : `${label} connected. Your creator profile is ready — get notified about UGC jobs in your niche.`,
                );
                return;
            }
            await exchangeTokensOnly(code, provider);
            finishSuccess(`${label} reconnected.`);
        } catch (err: unknown) {
            const msg =
                (err as { data?: { message?: string }; message?: string })?.data?.message ||
                (err as { message?: string })?.message ||
                `Could not reconnect ${label}.`;
            if (!/cancel|closed/i.test(String(msg))) {
                showToastMessage(msg, 'error');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleInstagramAutomationReconnect = async () => {
        try {
            setBusy(true);
            const oauthUrl = (await dispatchPromise({
                type: getInstagramAutomationOAuthUrl.type,
            })) as string;
            setBusy(false);
            const code = await openMetaOAuthPopup(oauthUrl);
            await handleCode(code, 'meta');
        } catch (err: unknown) {
            setBusy(false);
            const msg =
                (err as { data?: { message?: string }; message?: string })?.data?.message ||
                (err as { message?: string })?.message ||
                'Could not reconnect Instagram.';
            if (!/cancel|closed/i.test(String(msg))) {
                showToastMessage(msg, 'error');
            }
        }
    };

    const handleCreateFromConnectedInstagram = async () => {
        setBusy(true);
        try {
            await dispatchPromise(postInfluencerFromConnectedInstagram());
            finishSuccess(
                hasProfile
                    ? 'Instagram updated on your creator profile.'
                    : 'Creator profile created — get notified about UGC jobs in your niche.',
            );
        } catch (err: unknown) {
            const msg =
                (err as { data?: { message?: string }; message?: string })?.data?.message ||
                (err as { message?: string })?.message ||
                'Could not update creator profile.';
            showToastMessage(msg, 'error');
        } finally {
            setBusy(false);
        }
    };

    const profileCheckboxLabel = hasProfile
        ? `Update my creator profile with this ${label} account`
        : 'Create my creator profile';
    const profileCheckboxHint = hasProfile
        ? 'Adds or refreshes this account on your existing creator page (won’t create a second profile).'
        : 'Get notified about UGC jobs in your niche when brands look for creators like you.';

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: IG_AUTO_BG,
                    backgroundImage: 'none',
                    border: `1px solid ${IG_AUTO_BORDER}`,
                    borderRadius: 3,
                },
            }}>
            <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsOutlinedIcon sx={{ color: IG_AUTO_GOLD, fontSize: 20 }} />
                        <Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: IG_AUTO_WHITE }}>
                                {label} settings
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: IG_AUTO_MUTED, mt: 0.25 }}>
                                {displayUsername
                                    ? `Connected as ${displayUsername}`
                                    : `Reconnect or switch ${label} account`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} disabled={busy} size="small" sx={{ color: IG_AUTO_MUTED }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {loadingStatus ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} sx={{ color: IG_AUTO_GOLD }} />
                    </Box>
                ) : (
                    <>
                        {needsReconnect && (
                            <Typography sx={{ fontSize: 12, color: '#ffb74d', mb: 1.5, lineHeight: 1.45 }}>
                                Your Instagram connection needs attention — reconnect to restore posting and
                                comment-to-DM.
                            </Typography>
                        )}

                        {showCreatorProfileOption && (
                            <FormControlLabel
                                sx={{ alignItems: 'flex-start', mb: 2, mx: 0 }}
                                control={
                                    <Checkbox
                                        checked={linkCreatorProfile}
                                        onChange={e => setLinkCreatorProfile(e.target.checked)}
                                        disabled={busy}
                                        sx={{
                                            color: IG_AUTO_MUTED,
                                            pt: 0.25,
                                            '&.Mui-checked': { color: IG_AUTO_GOLD },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                color: IG_AUTO_WHITE,
                                                lineHeight: 1.35,
                                            }}>
                                            {profileCheckboxLabel}
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 12, color: IG_AUTO_MUTED, mt: 0.5, lineHeight: 1.45 }}>
                                            {profileCheckboxHint}
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {platform === 'instagram' && !authBaseUrl ? (
                                <Button
                                    disabled={busy}
                                    onClick={() => void handleInstagramAutomationReconnect()}
                                    variant="contained"
                                    sx={{
                                        bgcolor: IG_AUTO_GOLD,
                                        color: '#111',
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        borderRadius: 999,
                                        '&:hover': { bgcolor: '#e3aa00' },
                                    }}>
                                    {busy ? (
                                        <CircularProgress size={18} sx={{ color: '#111' }} />
                                    ) : (
                                        `Reconnect ${label}`
                                    )}
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <SocialLogin
                                        provider={
                                            platform === 'instagram'
                                                ? (igConnectProvider as 'meta' | 'instagram')
                                                : platform
                                        }
                                        authBaseUrl={authBaseUrl}
                                        onCodeReceived={code => {
                                            void handleCode(
                                                code,
                                                platform === 'instagram' ? String(igConnectProvider) : oauthProvider,
                                            );
                                        }}
                                        customButton={
                                            <Button
                                                disabled={busy}
                                                variant="contained"
                                                sx={{
                                                    bgcolor: IG_AUTO_GOLD,
                                                    color: '#111',
                                                    textTransform: 'none',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    borderRadius: 999,
                                                    '&:hover': { bgcolor: '#e3aa00' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'rgba(255,201,101,0.25)',
                                                        color: 'rgba(0,0,0,0.4)',
                                                    },
                                                }}>
                                                {busy ? (
                                                    <CircularProgress size={18} sx={{ color: '#111' }} />
                                                ) : (
                                                    `Reconnect ${label}`
                                                )}
                                            </Button>
                                        }
                                    />
                                </Box>
                            )}

                            {platform === 'instagram' &&
                                showCreatorProfileOption &&
                                linkCreatorProfile &&
                                status?.instagram_connected &&
                                !onOAuthCode && (
                                    <Button
                                        disabled={busy}
                                        onClick={() => void handleCreateFromConnectedInstagram()}
                                        sx={{
                                            color: IG_AUTO_GOLD,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            border: '1px solid rgba(255,201,101,0.35)',
                                            borderRadius: 999,
                                        }}>
                                        {hasProfile
                                            ? 'Update profile from Instagram'
                                            : 'Create profile without reconnecting'}
                                    </Button>
                                )}
                        </Box>
                    </>
                )}
            </Box>
        </Dialog>
    );
}

export function InstagramConnectionSettingsModal(
    props: Omit<PlatformConnectionSettingsModalProps, 'platform'> & { platform?: SocialPlatform },
) {
    return <PlatformConnectionSettingsModal {...props} platform={props.platform ?? 'instagram'} />;
}
